import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { DetroitGameState } from '../../types';
import { getIncomeReductionDetroit } from '../../types';
import { DETROIT_RULES } from '../../../data/map/detroit_bankruptcy_solo/config';
import { undoToTurnStart } from '../moves/undoToTurnStart';

/**
 * Phase 8: 収入減少フェーズ（Detroit固有）
 *
 * 各プレイヤーの収入レベルを収入レベルに応じて自動的に減少させます。
 * Detroit固有: 5ステップごとに減少値が+1
 * ロジック処理後、ユーザーがクリックするまで待機します。
 *
 * 減少テーブル（5ステップ制）:
 * - 0-5: 0（減少なし）
 * - 6-10: -1
 * - 11-15: -2
 * - 16-20: -3
 * - 21-25: -4
 * - 26-30: -5
 * - ...（以降同様）
 *
 * 参照:
 * - docs/specifications/logic/detroit_bankruptcy_solo/phase_08_income_reduction_override.md
 */
export const incomeReductionDetroitPhase: PhaseConfig<DetroitGameState> = {
  // Phase開始時に自動実行
  onBegin: ({ G, ctx }: { G: DetroitGameState; ctx: any }) => {
    debug.log('[incomeReductionDetroitPhase] ★★★ Phase 8: Income Reduction started ★★★');
    debug.log('[incomeReductionDetroitPhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase, currentPlayer: ctx.currentPlayer });

    // ソロプレイ用: Player 0の収入減少
    const player = G.players['0'];
    if (!player || player.isBankrupt) {
      // 倒産時は即座に次フェーズへ（結果表示なし）
      G.autoPhaseResult = null;
      return;
    }

    // 減少幅を計算（Detroit固有: 5ステップ制）
    const reduction = getIncomeReductionDetroit(player.incomeLevel);

    // 収入レベルを減少
    const oldIncomeLevel = player.incomeLevel;
    player.incomeLevel -= reduction;

    debug.log(`[incomeReductionDetroitPhase] Player 0 incomeLevel: ${oldIncomeLevel} → ${player.incomeLevel} (reduction: ${reduction})`);

    // 収入レベルが0を下回った場合（負の場合）は破産
    if (player.incomeLevel < 0) {
      player.isBankrupt = true;
      G.isGameOver = true;
      G.endReason = 'BANKRUPTCY';

      // 勝敗判定（Detroit固有: ターン7以上で破産なら勝利）
      player.finalScore = G.actualTurn;
      player.isVictorious = G.actualTurn >= DETROIT_RULES.victoryTurnThreshold;

      debug.log(`[incomeReductionDetroitPhase] ★ Player BANKRUPT at turn ${G.actualTurn}`);
      debug.log(`[incomeReductionDetroitPhase] ★ Victory: ${player.isVictorious ? 'YES' : 'NO'}`);

      // 破産の処理結果を保存
      G.autoPhaseResult = {
        phase: 'incomeReduction',
        message: player.isVictorious
          ? `VICTORY! Survived ${G.actualTurn} turns!`
          : `DEFEAT. Bankrupt on turn ${G.actualTurn}.`,
        floatingText: player.isVictorious ? `VICTORY!` : `BANKRUPT!`,
        details: { reduction, bankrupt: true },
      };
    } else {
      // 処理結果を保存（UI表示用）
      G.autoPhaseResult = {
        phase: 'incomeReduction',
        message: 'Reducing income based on current level (5-step rule)...',
        floatingText: reduction > 0 ? `-${reduction} Income` : 'No reduction',
        details: { reduction },
      };
    }

    debug.log('[incomeReductionDetroitPhase] ★ Auto-processing complete, waiting for user click');
  },

  moves: {
    // ユーザークリックでフェーズ進行
    proceedAutoPhase: ({ G, events }: { G: DetroitGameState; events: any }) => {
      // Phase内でのみ呼び出し可能（Boardgame.ioが保証）
      G.autoPhaseResult = null;
      events.endPhase();
    },
    undoToTurnStart,
  },

  // 倒産プレイヤーは自動的に次フェーズへ、または Undo 強制遷移
  endIf: ({ G }: { G: DetroitGameState }) => {
    // Undo 強制遷移フラグが立っている場合は即座に終了
    if (G.forcePhaseToIssueShares) {
      debug.log('[incomeReductionDetroitPhase] Force transition to issueShares (Undo)');
      return true;
    }

    const player = G.players['0'];
    if (!player || player.isBankrupt) {
      return true;
    }
    return false;
  },

  onEnd: ({ G, ctx }: { G: DetroitGameState; ctx: any }) => {
    debug.log('[incomeReductionDetroitPhase] ★★★ onEnd called - Phase 8 ended ★★★');
    debug.log('[incomeReductionDetroitPhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase });
    debug.log('[incomeReductionDetroitPhase] ★★★ Next phase should be: refillGoods');

    // 自動フェーズ履歴に追加（UIアニメーション用）
    if (!G.completedAutoPhases) {
      G.completedAutoPhases = [];
    }
    G.completedAutoPhases.push('incomeReduction');
  },

  // Phase 9 へ遷移、または Undo 時は issueShares へ
  next: ({ G }: { G: DetroitGameState }) => {
    if (G.forcePhaseToIssueShares) {
      return 'issueShares';
    }
    return 'refillGoods';
  }
};
